import axios from 'axios';
import config from '../Config';
import { handle401Unauthorized } from '../utils/Helper';
import authHeader from './auth-header';

const API_URL = config.API_URL;
const API_VERSION = config.API_VERSION;

// Create a separate axios instance for Plant Type module to handle 401 errors
const plantTypeAxiosInstance = axios.create();

// Add response interceptor to handle 401 errors for Plant Type module
plantTypeAxiosInstance.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      handle401Unauthorized();
    }
    return Promise.reject(error);
  }
);

// Plant Type APIs
const getPlantTypeList = (page, pagesize, search, ordering, status) => {
  return plantTypeAxiosInstance.get(API_URL + '/api/' + API_VERSION + '/plant-types/', {
    headers: authHeader(),
    params: {
      page: page,
      pagesize: pagesize,
      ...(search && { search: search }),
      ...(ordering && { ordering: ordering }),
      ...(status && { status: status }),
    },
  });
};

const getPlantTypeDropdown = () => {
  return plantTypeAxiosInstance.get(`${API_URL}/api/${API_VERSION}/plant-types/dropdown/`, {
    headers: authHeader(),
  });
};

const getPlantTypeById = id => {
  return plantTypeAxiosInstance.get(`${API_URL}/api/${API_VERSION}/plant-types/${id}/`, {
    headers: authHeader(),
  });
};

const addPlantType = formData => {
  return plantTypeAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/plant-types/`,
    formData,
    { headers: authHeader() },
  );
};

const updatePlantType = (id, formPayload) => {
  return plantTypeAxiosInstance.put(
    `${API_URL}/api/${API_VERSION}/plant-types/${id}/`,
    formPayload,
    { headers: authHeader() },
  );
};

const patchPlantType = (id, formPayload) => {
  return plantTypeAxiosInstance.patch(
    `${API_URL}/api/${API_VERSION}/plant-types/${id}/`,
    formPayload,
    { headers: authHeader() },
  );
};

const changePlantTypeStatus = (id, status) => {
  return plantTypeAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/plant-types/${id}/change-status/`,
    { status: status },
    { headers: authHeader() },
  );
};

const deletePlantType = id => {
  return plantTypeAxiosInstance.delete(
    `${API_URL}/api/${API_VERSION}/plant-types/${id}/`,
    { headers: authHeader() },
  );
};

// Plant Capability APIs
const getPlantCapabilityList = (page, pagesize, search, ordering) => {
  return plantTypeAxiosInstance.get(API_URL + '/api/' + API_VERSION + '/plant-capabilities/', {
    headers: authHeader(),
    params: {
      page: page,
      pagesize: pagesize,
      search: search,
      ordering: ordering,
    },
  });
};

const getPlantTypeCapabilities = (plantTypeId, page, pagesize) => {
  return plantTypeAxiosInstance.get(
    `${API_URL}/api/${API_VERSION}/plant-type-capabilities/plant-type/${plantTypeId}/capabilities/`,
    {
      headers: authHeader(),
      params: {
        page: page,
        pagesize: pagesize,
      },
    },
  );
};

const assignPlantTypeCapability = formData => {
  return plantTypeAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/plant-type-capabilities/`,
    formData,
    { headers: authHeader() },
  );
};

const removePlantTypeCapability = id => {
  return plantTypeAxiosInstance.delete(
    `${API_URL}/api/${API_VERSION}/plant-type-capabilities/${id}/`,
    { headers: authHeader() },
  );
};

const getPlantTypeArchiveList = (page, pagesize, search, ordering) => {
  return plantTypeAxiosInstance.get(`${API_URL}/api/${API_VERSION}/plant-types-archive/`, {
    headers: authHeader(),
    params: {
      page: page,
      pagesize: pagesize,
      ...(search && { search: search }),
      ...(ordering && { ordering: ordering }),
    },
  });
};

const getPlantTypeArchiveById = id => {
  return plantTypeAxiosInstance.get(`${API_URL}/api/${API_VERSION}/plant-types-archive/${id}/`, {
    headers: authHeader(),
  });
};

const restorePlantType = ids => {
  return plantTypeAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/plant-types/bulk-restore/`,
    { ids: Array.isArray(ids) ? ids : [ids] },
    { headers: authHeader() },
  );
};
const bulkArchivePlantType = ids => {
  return plantTypeAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/plant-types/bulk-archive/`,
    { ids: Array.isArray(ids) ? ids : [ids] },
    { headers: authHeader() },
  );
};
const plantTypeService = {
  getPlantTypeList,
  getPlantTypeDropdown,
  getPlantTypeById,
  addPlantType,
  updatePlantType,
  patchPlantType,
  changePlantTypeStatus,
  deletePlantType,
  getPlantCapabilityList,
  getPlantTypeCapabilities,
  assignPlantTypeCapability,
  removePlantTypeCapability,
  getPlantTypeArchiveList,
  getPlantTypeArchiveById,
  restorePlantType,
  bulkArchivePlantType,
};

export default plantTypeService;

